import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmModal } from '@microservices/wiskey-react-components'
import {
  DataGrid,
  GridCellParams,
  GridColDef,
} from '@microservices/wiskey-react-table-component'
import { Grid } from '@mui/material'

import { generateDataGridActionsColumnForStyleSettings } from '@pages/ConfigStyleSettings/helpers'

import { FLEXIBLE } from '@constants'

type TableWithActionsProps = {
  columns: GridColDef<any>[]
  rows: readonly any[]
  onDelete?: (id: string | number) => void
  onEdit?: (id: string | number) => void
  loading?: boolean
}

export const TableWithActions: FC<TableWithActionsProps> = ({
  columns,
  rows,
  onDelete,
  onEdit,
  loading,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [currentRowId, setCurrentRowId] = useState<string | number | undefined>(undefined)
  const { t } = useTranslation()

  const handleEdit = (params: GridCellParams<any, any>) => {
    if (params.row?.id != null) {
      onEdit?.(params.row.id)
    }
  }

  const handleClickDelete = (params: GridCellParams<any, any>) => {
    if (params.row?.id != null) {
      setShowDeleteModal(true)
      setCurrentRowId(params.row.id)
    }
  }

  const handleDelete = () => {
    if (currentRowId != null) {
      onDelete?.(currentRowId)
      setShowDeleteModal(false)
      setCurrentRowId(undefined)
    }
  }

  const handleClose = () => {
    setShowDeleteModal(false)
    setCurrentRowId(undefined)
  }

  const columnsWithActions = useMemo(
    () => [
      ...columns,
      generateDataGridActionsColumnForStyleSettings({
        onEdit: onEdit ? handleEdit : undefined,
        onDelete: onDelete ? handleClickDelete : undefined,
      }),
    ],
    [columns, onEdit, onDelete],
  )

  return (
    <Grid container sx={{ ...FLEXIBLE}}>
      {showDeleteModal && (
        <ConfirmModal
          actionBtnText={t('modal.delete.btn')}
          isShow={showDeleteModal}
          text={t('modal.delete.warning')}
          title={t('modal.delete.title')}
          containerStyle={{
            px: 0.5,
            py: 0.5,
          }}
          onClose={handleClose}
          onConfirm={handleDelete}
        />
      )}

      <Grid container item sx={FLEXIBLE}>
        <DataGrid
          hideFooter
          columns={columnsWithActions}
          loading={loading}
          rows={rows}
          sx={{ borderRadius: 0 }}
        />
      </Grid>
    </Grid>
  )
}
